import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CalendarRange } from "lucide-react";
import { toast } from "sonner";
import { runRecipeRange, waitForRun } from "@/lib/api";

function yesterdayIso() {
  const d = new Date(); d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}

export default function DateRangeRunDialog({ recipe, onRunning, triggerLabel = "Histórico" }) {
  const [open, setOpen] = useState(false);
  const [startDate, setStartDate] = useState(yesterdayIso());
  const [endDate, setEndDate] = useState(yesterdayIso());
  const [running, setRunning] = useState(false);
  const nav = useNavigate();

  async function onRun() {
    if (!startDate || !endDate) return toast.error("Define as duas datas");
    setRunning(true);
    onRunning?.(true);
    try {
      const run = await waitForRun(await runRecipeRange(recipe.id, startDate, endDate));
      if (run.status === 'error') toast.error(`A captura falhou: ${run.error || 'erro desconhecido'}`);
      else toast.success(`Run concluída: ${run.rows?.length || 0} linhas`);
      setOpen(false);
      nav(`/runs/${run.id}`);
    } catch (e) {
      toast.error(`Falha: ${e?.response?.data?.detail || e.message}`);
    } finally { setRunning(false); onRunning?.(false); }
  }

  const hasDateStep = (recipe.steps || []).some(s => s.type === 'fill' && s.is_date);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid="recipe-card-range-run-button">
          <CalendarRange className="h-3.5 w-3.5 mr-1"/> {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Recolha por intervalo de datas</DialogTitle>
          <DialogDescription>Para cada dia entre o início e o fim, o sistema substitui os campos de data marcados na recipe e faz uma recolha. Resultados são agregados com uma coluna "Data".</DialogDescription>
        </DialogHeader>
        {!hasDateStep && (
          <div className="text-xs rounded-lg border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 p-3 text-foreground">
            ⚠️ Esta recipe ainda não tem passos <span className="mono">fill</span> marcados como “data”. Edita a recipe, abre os passos de preenchimento de datas e ativa o toggle “Campo de data” para que o sistema saiba quais substituir.
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="range-start" className="text-xs">Data início</Label>
            <Input id="range-start" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} data-testid="range-start-date-input"/>
          </div>
          <div>
            <Label htmlFor="range-end" className="text-xs">Data fim</Label>
            <Input id="range-end" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} data-testid="range-end-date-input"/>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => { const d = yesterdayIso(); setStartDate(d); setEndDate(d); }}>Ontem</Button>
          <Button size="sm" variant="outline" onClick={() => { const d = new Date(); d.setDate(d.getDate() - 7); setStartDate(d.toISOString().slice(0,10)); setEndDate(yesterdayIso()); }}>Últimos 7 dias</Button>
          <Button size="sm" variant="outline" onClick={() => { const d = new Date(); d.setDate(d.getDate() - 30); setStartDate(d.toISOString().slice(0,10)); setEndDate(yesterdayIso()); }}>Últimos 30 dias</Button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={onRun} disabled={running} data-testid="range-run-confirm-button">{running ? "A correr… (pode demorar)" : "Correr para intervalo"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
